/* eslint-disable no-nested-ternary */
import React, { useEffect, useState, useCallback, useMemo, useContext, useRef } from 'react';
import { ThemeProvider } from 'styled-components';
import moment from 'moment';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import BasicIcon from '../BasicIcon/BasicIcon';
import { DatePickerProps, DayType } from './types';
import { ID } from '../utils/arrays';
import DateOverlay from './components/DateOverlay/DateOverlay';
import { DayTypeEnum } from './components/Day/types';
import { ArrowContainer } from './styles';
import { InputContainer } from '../Input/styles';
import Input from '../Input/Input';

/**
 * Datepicker
 * @param placeholder Represent the placeholder string when the input is empty
 * @param label
 * @param minDate The minimum date that the date picker can't access
 * @param maxDate The maximum date that the date picker can't access
 * @param singleSelection A boolean that select one date only
 */
const DatePicker: React.FC<DatePickerProps> = (props) => {
  const {
    input,
    placeholder,
    label,
    singleSelection,
    disabled,
    readOnly,
    minDate,
    maxDate,
    outputFormat = 'DD/MM/YY',
    meta,
    testId = 'datepicker',
    size,
    variant = 'underline',
    disabledWeekDays = [],
    overlayPosition = 'auto',
    isMobile,
    overAll = false,
    onChange,
    onFocus,
    onBlur,
    onMouseDown,
    inputRef,
  } = props;
  const defaultInputRef = useRef<HTMLInputElement>(null);
  const localInputRef: React.Ref<HTMLInputElement> = inputRef || defaultInputRef;
  const { actualTheme, isMobile: mobileTheme } = useContext(ThemeContext);
  const useMobileVersion = useMemo(() => isMobile ?? mobileTheme, [isMobile, mobileTheme]);
  const [momentInstance, setInstance] = useState<moment.Moment | null>(null);
  const [monthUpdated, setMonthUpdated] = useState(false);
  const [infoData, setInfoData] = useState({
    displayDate: '',
  });
  const [generatedId] = useState(ID());
  const [value, setValue] = useState(
    input?.value &&
      (singleSelection ? input?.value : `${input?.value.from} al ${input?.value.until}`),
  );
  const [isOpen, setIsOpen] = useState(false);
  const hasError = useMemo(() => meta && meta.invalid && meta.touched, [meta]);
  const inputSize = useMemo(() => {
    if (!size && useMobileVersion) return 'small';
    return size || 'standard';
  }, [size, useMobileVersion]);
  const inputHeight = useMemo(() => {
    const s = inputSize || 'standard';
    const h = actualTheme.input.sizes[s].height[variant];
    if (variant === 'top-aligned-label') return h + 20;
    return h;
  }, [actualTheme, inputSize, variant]);

  useEffect(() => {
    if (singleSelection) setValue(input?.value || '');
    else if (input?.value?.from && input?.value?.until)
      setValue(`${input?.value.from} al ${input?.value.until}`);
    else if (input?.value?.from) setValue(`${input?.value?.from}`);
    else setValue('');
  }, [input?.value, singleSelection]);

  const handleClick = useCallback(() => {
    if (!disabled && !readOnly) {
      setIsOpen((prev) => !prev);
    }
    if (onMouseDown) onMouseDown();
  }, [disabled, onMouseDown, readOnly]);

  const handleChange = useCallback(
    (date: any) => {
      let newDate;
      if (singleSelection) {
        newDate = moment(date, 'DD/MM/YYYY').format(outputFormat);
        setIsOpen(false);
      } else {
        newDate = {
          from: moment(date.from, 'DD/MM/YYYY').format(outputFormat),
          until: date.until ? moment(date.until, 'DD/MM/YYYY').format(outputFormat) : '',
        };
        if (date.until) {
          setIsOpen(false);
        }
      }
      if (input) input.onChange(newDate);
      if (onChange) onChange(newDate);
    },
    [input, outputFormat, singleSelection, onChange],
  );
  const makeDays = useMemo(() => {
    if (!momentInstance) return [];
    const weekDays: Array<number> = [6, 0, 1, 2, 3, 4, 5];
    return weekDays.map((day: number) => momentInstance.clone().weekday(day).format('dd'));
  }, [momentInstance]);

  const isAvailable = useCallback(
    (num: number) => {
      const actualDay = momentInstance
        .clone()
        .startOf('month')
        .add(num - 1, 'days');
      if (minDate && actualDay.isBefore(moment(minDate))) {
        return false;
      }
      if (maxDate && actualDay.isAfter(moment(maxDate))) {
        return false;
      }
      if (disabledWeekDays.includes(actualDay.day())) {
        return false;
      }
      return true;
    },
    [momentInstance, minDate, maxDate, disabledWeekDays],
  );

  const getDayType = useCallback(
    (num: number) => {
      const actualDay = momentInstance
        .clone()
        .startOf('month')
        .add(num - 1, 'days');
      if (actualDay.isBefore(moment(), 'day')) {
        return DayTypeEnum.LastDay;
      }
      if (actualDay.isAfter(moment(), 'day')) {
        return DayTypeEnum.FutureDay;
      }
      return DayTypeEnum.ActualDay;
    },
    [momentInstance],
  );

  const isWeekend = useCallback(
    (num: number) => {
      const actualDay = momentInstance.clone().startOf('month').add(num, 'days');
      if (actualDay.day() <= 1) {
        return true;
      }
      return false;
    },
    [momentInstance],
  );

  const createGrid = useMemo(() => {
    if (!momentInstance) return [];
    const firstDayDate = moment(momentInstance).startOf('month');
    const initialEmptyCells = (firstDayDate.weekday() + 1) % 7;
    const lastDayDate = moment(momentInstance).endOf('month');
    const lastEmptyCells = 6 - ((lastDayDate.weekday() + 1) % 7);
    const daysInMonth = momentInstance.daysInMonth();
    const arrayLength = initialEmptyCells + lastEmptyCells + daysInMonth;
    const gridArr: DayType[] = [];
    for (let i = 0; i < arrayLength; i += 1) {
      const obj: DayType = {
        id: ID(),
        value: 0,
        available: false,
        type: DayTypeEnum.LastDay,
        weekend: false,
      };
      if (i >= initialEmptyCells && i <= initialEmptyCells + daysInMonth - 1) {
        obj.value = i - initialEmptyCells + 1;
        obj.available = isAvailable(i - initialEmptyCells + 1);
        obj.type = getDayType(i - initialEmptyCells + 1);
        obj.weekend = isWeekend(i - initialEmptyCells + 1);
      }
      gridArr.push(obj);
    }
    return gridArr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [momentInstance, monthUpdated, isAvailable, getDayType, isWeekend]);

  const goBack = useCallback(() => {
    momentInstance.add(-1, 'month');
    setMonthUpdated((prev) => !prev);
    setInfoData({
      displayDate: momentInstance.format('MMMM YYYY'),
    });
  }, [momentInstance]);

  const goFront = useCallback(() => {
    momentInstance.add(1, 'month');
    setMonthUpdated((prev) => !prev);
    setInfoData({
      displayDate: momentInstance.format('MMMM YYYY'),
    });
  }, [momentInstance]);

  useEffect(() => {
    if (momentInstance && isOpen) return;

    let initialDate = new Date();
    if (input?.value && singleSelection) initialDate = input?.value;
    else initialDate = input?.value?.from || new Date();

    const newInstance = moment(initialDate, 'DD/MM/YYYY');
    newInstance.locale('es');
    setInfoData({
      displayDate: newInstance.format('MMMM YYYY'),
    });
    setInstance(newInstance);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, singleSelection, input?.value]);

  const deselect = useCallback(() => {
    if (disabled || readOnly) return;
    let newDate: string | object = '';
    if (!singleSelection) {
      newDate = {
        from: '',
        until: '',
      };
    }
    if (input) input.onChange(newDate);
    if (onChange) onChange(newDate);
    setIsOpen(false);
  }, [singleSelection, input, disabled, readOnly, onChange]);

  const renderChevronIcon = useCallback(
    () => (
      <ArrowContainer
        about="chevron"
        onClick={handleClick}
        isOpen={isOpen}
        disabled={readOnly || disabled}
        data-testid={`${testId}-chevron`}
      >
        <BasicIcon
          size="small"
          name="chevron-down"
          color={
            hasError
              ? actualTheme.status.error.color
              : disabled
              ? actualTheme.grayB8
              : actualTheme.gray80
          }
        />
      </ArrowContainer>
    ),
    [
      handleClick,
      isOpen,
      readOnly,
      disabled,
      testId,
      hasError,
      actualTheme.status.error.color,
      actualTheme.grayB8,
      actualTheme.gray80,
    ],
  );

  return (
    <ThemeProvider theme={actualTheme}>
      <InputContainer id="datepicker-id">
        <Input
          id={generatedId}
          typingAllowed={false}
          disabled={disabled}
          readOnly={readOnly}
          value={value}
          placeholder={placeholder}
          input={input}
          meta={meta}
          label={label}
          autoComplete="off"
          testId={testId}
          size={size}
          variant={variant}
          isOpen={isOpen}
          pointer
          rightAddOn={renderChevronIcon()}
          onMouseDown={handleClick}
          onClear={deselect}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          inputRef={localInputRef}
        />
        <DateOverlay
          isOpen={isOpen}
          isMobile={useMobileVersion}
          momentInstance={momentInstance}
          dayGrid={createGrid}
          makeDays={makeDays}
          target={generatedId}
          goBack={goBack}
          goFront={goFront}
          setIsOpen={setIsOpen}
          onSelection={handleChange}
          singleSelection={singleSelection}
          displayDate={infoData.displayDate}
          inputValue={input?.value}
          targetHeight={inputHeight}
          overlayPosition={overlayPosition}
          label={label}
          placeholder={placeholder}
          overAll={overAll}
          onClose={handleClick}
          testId={testId}
        />
      </InputContainer>
    </ThemeProvider>
  );
};

export default DatePicker;
